import React, { useState } from "react";

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
  }
}

const Contact: React.FC = () => {
  const initialFormData = {
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
    website: "",
  };
  const [formData, setFormData] = useState({
    ...initialFormData,
  });
  const [submitStatus, setSubmitStatus] = useState<
    "idle" | "sending" | "success" | "error"
  >("idle");
  const [submitMessage, setSubmitMessage] = useState("");

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitStatus("sending");
    setSubmitMessage("");

    try {
      const response = await fetch("/api/contact", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      });
      const result = await response.json().catch(() => null);

      if (!response.ok) {
        throw new Error(
          result?.error ||
            "No pudimos enviar el mensaje. Inténtalo nuevamente."
        );
      }

      setFormData(initialFormData);
      setSubmitStatus("success");
      setSubmitMessage(
        "Mensaje enviado. Nos pondremos en contacto contigo pronto."
      );
      window.gtag?.("event", "conversion", {
        send_to: "AW-18439351966/Y86tCKWT1vQcEJ7dyNhE",
      });
    } catch (error) {
      setSubmitStatus("error");
      setSubmitMessage(
        error instanceof Error && error.message.startsWith("No ")
          ? error.message
          : "No pudimos enviar el mensaje. Inténtalo nuevamente."
      );
    }
  };

  return (
    <div className="py-16 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold mb-4">CONTACTO</h1>
          <p className="text-gray-600 max-w-3xl mx-auto">
            Estamos aquí para ayudarte. Contáctanos para una consulta gratuita.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
          {/* Contact Form */}
          <div className="bg-white rounded-lg shadow-lg p-8">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label
                  htmlFor="name"
                  className="block text-sm font-medium text-gray-700"
                >
                  Nombre completo
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  maxLength={100}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  required
                />
              </div>

              <div>
                <label
                  htmlFor="email"
                  className="block text-sm font-medium text-gray-700"
                >
                  Correo electrónico
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  maxLength={254}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  required
                />
              </div>

              <div>
                <label
                  htmlFor="phone"
                  className="block text-sm font-medium text-gray-700"
                >
                  Teléfono
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  maxLength={40}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                />
              </div>

              <div>
                <label
                  htmlFor="subject"
                  className="block text-sm font-medium text-gray-700"
                >
                  Asunto
                </label>
                <select
                  id="subject"
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  required
                >
                  <option value="">Seleccione un asunto</option>
                  <option value="consulta">Consulta general</option>
                  <option value="cita">Solicitar cita</option>
                  <option value="servicio">Información de servicios</option>
                  <option value="otro">Otro</option>
                </select>
              </div>

              <div>
                <label
                  htmlFor="message"
                  className="block text-sm font-medium text-gray-700"
                >
                  Mensaje
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  minLength={10}
                  maxLength={5000}
                  rows={4}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  required
                ></textarea>
              </div>

              <div className="absolute -left-[10000px]" aria-hidden="true">
                <label htmlFor="website">Sitio web</label>
                <input
                  type="text"
                  id="website"
                  name="website"
                  value={formData.website}
                  onChange={handleChange}
                  tabIndex={-1}
                  autoComplete="off"
                />
              </div>

              {submitMessage && (
                <p
                  className={
                    submitStatus === "success"
                      ? "text-sm text-green-700"
                      : "text-sm text-red-700"
                  }
                  role={submitStatus === "error" ? "alert" : "status"}
                >
                  {submitMessage}
                </p>
              )}

              <button
                type="submit"
                className="w-full rounded-md bg-primary px-6 py-3 font-medium text-white transition-colors hover:bg-primary-dark disabled:cursor-not-allowed disabled:opacity-60"
                disabled={submitStatus === "sending"}
              >
                {submitStatus === "sending"
                  ? "Enviando..."
                  : "Enviar mensaje"}
              </button>
            </form>
          </div>

          {/* Contact Information */}
          <div className="space-y-8">
            <div className="bg-white rounded-lg shadow-lg p-8">
              <h2 className="text-2xl font-bold mb-6">
                Información de contacto
              </h2>
              <div className="space-y-4">
                <p className="flex items-center text-gray-600">
                  <span className="font-medium mr-2">Dirección:</span>
                  Cartagena, Colombia
                </p>
                <p className="flex items-center text-gray-600">
                  <span className="font-medium mr-2">Email:</span>
                  <a
                    href="mailto:T-DEFENDEMOS@outlook.com"
                    className="hover:text-blue-700 hover:underline"
                  >
                    T-DEFENDEMOS@outlook.com
                  </a>
                </p>
                <p className="flex items-center text-gray-600">
                  <span className="font-medium mr-2">Teléfono:</span>
                  +57 (310) 275-8946
                </p>
                <p className="flex items-center text-gray-600">
                  <span className="font-medium mr-2">Horario:</span>
                  Lunes a Viernes: 8:00 AM - 6:00 PM
                </p>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-lg p-8">
              <h2 className="text-2xl font-bold mb-6">Ubicación</h2>
              <div className="aspect-w-16 aspect-h-9">
                {/* Add Google Maps iframe or custom map component here */}
                <div className="w-full h-64 bg-gray-200 rounded-lg"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Contact;
